import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, Loader2, Mail, Paperclip, RefreshCw, Eye } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { Btn, Card, EmptyState, LabeledSelect, PageHeader, Pagination } from "@/components/dashboard/Ui";

const TYPES = { new_submission: "New submission", appointment_assigned: "Appointment assigned", appointment_rescheduled: "Appointment rescheduled", appointment_cancelled: "Appointment cancelled", invitation: "Invitation", password_reset: "Password reset" };

export default function EmailsPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [preview, setPreview] = useState(null);

  const { data: smtp } = useQuery({ queryKey: ["smtp-status"], queryFn: async () => (await api.get("/emails/status")).data });
  const { data, isLoading } = useQuery({ queryKey: ["emails", status, type, page], queryFn: async () => (await api.get("/emails", { params: { status: status || undefined, type: type || undefined, page, page_size: 50 } })).data, refetchInterval: 30_000 });
  const { data: previewData } = useQuery({ queryKey: ["email", preview], queryFn: async () => (await api.get(`/emails/${preview}`)).data.email, enabled: !!preview });

  const retry = useMutation({
    mutationFn: async (id) => (await api.post(`/emails/${id}/retry`)).data,
    onSuccess: (res) => {
      toast[res.email.status === "sent" ? "success" : "error"](res.email.status === "sent" ? "Email sent" : `Still failing: ${res.email.error}`);
      qc.invalidateQueries({ queryKey: ["emails"] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div data-testid="emails-page">
      <PageHeader title="Email log" description="Every notification the system generates, with delivery status. Failed or pending emails can be retried once SMTP is configured." />

      {smtp && (
        <div className={`mb-4 flex flex-wrap items-center gap-3 rounded-[10px] border px-4 py-3 text-sm ${smtp.configured ? "border-[hsl(140_25%_80%)] bg-[hsl(140_35%_92%)] text-[hsl(140_43%_28%)]" : "border-[hsl(46_55%_80%)] bg-[hsl(46_90%_94%)] text-[hsl(30_70%_25%)]"}`} data-testid="smtp-status-banner">
          {smtp.configured ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <span>
            {smtp.configured ? (
              <>
                SMTP connected as <strong>{smtp.user}</strong> via {smtp.host}:{smtp.port}. Emails are sent from “{smtp.from_name}” &lt;{smtp.from_email}&gt;.
              </>
            ) : (
              <>
                SMTP not configured: set <code className="rounded bg-white/60 px-1">SMTP_PASSWORD</code> for <strong>{smtp.user}</strong> ({smtp.host}) in <code className="rounded bg-white/60 px-1">backend/.env</code> and restart. Until then emails are stored here as <em>pending</em> and retried automatically every 10 minutes once configured.
              </>
            )}
          </span>
          {smtp.pending > 0 && <span className="ms-auto rounded-full bg-white/70 px-2.5 py-0.5 text-xs font-semibold">{smtp.pending} waiting</span>}
        </div>
      )}

      <div className="emerge-card mb-4 flex flex-wrap items-center gap-3 p-4">
        <LabeledSelect id="e-status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="min-w-[160px]" data-testid="emails-filter-status-select" aria-label="Filter by status">
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </LabeledSelect>
        <LabeledSelect id="e-type" value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} className="min-w-[220px]" data-testid="emails-filter-type-select" aria-label="Filter by type">
          <option value="">All types</option>
          {Object.entries(TYPES).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </LabeledSelect>
        <span className="ms-auto text-sm text-muted-foreground">{data?.total ?? 0} emails</span>
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (data?.items || []).length === 0 ? (
          <EmptyState icon={Mail} title="No emails yet" description="Notifications will appear here as submissions arrive and appointments are assigned." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm" data-testid="email-log-table">
              <thead className="bg-secondary/70 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-start font-semibold">Created</th>
                  <th className="px-4 py-3 text-start font-semibold">Type</th>
                  <th className="px-4 py-3 text-start font-semibold">Subject</th>
                  <th className="px-4 py-3 text-start font-semibold">Recipients</th>
                  <th className="px-4 py-3 text-start font-semibold">Status</th>
                  <th className="px-4 py-3 text-end font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((e) => (
                  <tr key={e.id} className="border-t border-border table-row-hover align-top" data-testid="email-row">
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{fmtDateTime(e.created_at)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{TYPES[e.type] || e.type}</td>
                    <td className="px-4 py-3 font-medium text-foreground">
                      {e.subject}
                      {e.attachments?.length > 0 && (
                        <span className="ms-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Paperclip className="h-3 w-3" /> {e.attachments.map((a) => a.filename).join(", ")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {e.to.slice(0, 2).join(", ")}
                      {e.to.length > 2 && <span> +{e.to.length - 2} more</span>}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={e.status} />
                      {e.error && <p className="mt-1 max-w-[260px] text-xs text-muted-foreground">{e.error}</p>}
                      {e.attempts > 0 && <p className="text-xs text-muted-foreground">{e.attempts} attempt{e.attempts > 1 ? "s" : ""}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Btn variant="ghost" size="icon" title="Preview" onClick={() => setPreview(e.id)} data-testid="email-preview-button">
                          <Eye />
                        </Btn>
                        {e.status !== "sent" && (
                          <Btn variant="ghost" size="icon" title="Retry" onClick={() => retry.mutate(e.id)} disabled={retry.isPending || !smtp?.configured} data-testid="email-retry-button">
                            <RefreshCw className={retry.isPending ? "animate-spin" : ""} />
                          </Btn>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && <Pagination page={page} pageSize={50} total={data.total} onChange={setPage} />}
      </Card>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl bg-white p-0" data-testid="email-preview-dialog">
          <DialogHeader className="border-b border-border px-6 py-4">
            <DialogTitle className="text-lg">{previewData?.subject || "Email preview"}</DialogTitle>
            <DialogDescription>
              To: {previewData?.to?.join(", ")} {previewData?.attachments?.length ? `- attachment: ${previewData.attachments.map((a) => a.filename).join(", ")}` : ""}
            </DialogDescription>
          </DialogHeader>
          {previewData ? <iframe title="Email preview" srcDoc={previewData.html} className="h-[70vh] w-full bg-[#f4f7fa]" sandbox="" /> : <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
